/**
 * 화면 그룹 — 콘솔이 그룹을 다룰 때의 순수 계산.
 *
 * 그룹은 화면 묶음이다. 묶인 화면에 배정이 없으면 그룹 편성이 나가고(`packages/core/src/resolve.ts` 의 그룹 배정),
 * 캠페인·긴급의 대상이 되고, 계정의 담당 범위가 된다(`apps/server/src/services/scope.ts`). 서버 API 는 처음부터
 * 있었지만 콘솔에는 이미 있는 그룹을 고르는 칸뿐이라, 설명서가 안내하는 "사옥마다 그룹을 만들어 담당자에게 맡긴다"
 * 를 콘솔만으로 할 수 없었다(2026-10-19 기능 점검 G1).
 */
import {
  ApiCallError,
  type AccountUser,
  type Assignment,
  type Campaign,
  type Group,
  type Playlist,
  type Schedule,
} from './api/client.js';
import { compareLocationNames } from './components/locationOptions.js';

/**
 * 그룹 편성이 있는 그룹의 id — 그 그룹의 화면은 자기 배정이 없어도 틀 것이 있다.
 *
 * 그룹에 들기만 해서는 틀 것이 생기지 않는다. 서버의 '배정 없음' 판정(`apps/server/src/services/assigned.ts`)과 같은 기준.
 */
export function plannedGroupIds(groups: readonly Pick<Group, 'id' | 'assignment'>[]): Set<string> {
  return new Set(groups.filter((group) => group.assignment.kind !== 'none').map((group) => group.id));
}

/** 그룹 이름 순서 — 위치와 같다(가나다순, 숫자는 크기대로). 서버는 글자 그대로 세워 "2공장" 이 "10공장" 뒤에 선다. */
export function sortGroups<T extends Pick<Group, 'name'>>(groups: readonly T[]): T[] {
  return [...groups].sort((a, b) => compareLocationNames(a.name, b.name));
}

/**
 * 그룹 이름을 쓸 수 있는가 — 쓸 수 없으면 그 까닭, 쓸 수 있으면 null.
 *
 * 다른 그룹과 같은 이름은 서버도 409 로 막지만, 누르기 전에 말한다. 캠페인 대상·담당 범위를 이름으로 고르는데
 * 같은 이름이 둘이면 어느 것을 골랐는지 알 수 없다. `selfId` 는 고치는 중인 그룹 — 제 이름은 겹침이 아니다.
 */
export function groupNameProblem(draft: string, groups: readonly Pick<Group, 'id' | 'name'>[], selfId: string | null): string | null {
  const name = draft.trim();
  if (name === '') return '이름을 적으세요';
  if (name.length > 200) return '그룹 이름은 200자까지입니다';
  if (groups.some((group) => group.id !== selfId && group.name === name)) return '같은 이름의 그룹이 이미 있습니다';
  return null;
}

/** 편성을 드롭다운 값으로 — `playlist:<id>` · `schedule:<id>` · 없으면 빈 문자열. */
export function assignmentKey(assignment: Assignment): string {
  if (assignment.kind === 'playlist' && assignment.playlist_id) return `playlist:${assignment.playlist_id}`;
  if (assignment.kind === 'schedule' && assignment.schedule_id) return `schedule:${assignment.schedule_id}`;
  return '';
}

/** 드롭다운 값을 편성으로(`assignmentKey` 의 반대). 모르는 값은 편성 없음이다. */
export function assignmentFromKey(key: string): Assignment {
  const [kind, id] = key.split(':');
  if (kind === 'playlist' && id) return { kind: 'playlist', playlist_id: id, schedule_id: null };
  if (kind === 'schedule' && id) return { kind: 'schedule', playlist_id: null, schedule_id: id };
  return { kind: 'none', playlist_id: null, schedule_id: null };
}

/** 편성의 이름 — 없으면 null. 이름을 못 찾으면(지워졌거나 아직 못 받았다) 종류만 말한다. */
export function assignmentName(
  assignment: Assignment,
  playlists: readonly Pick<Playlist, 'id' | 'name'>[],
  schedules: readonly Pick<Schedule, 'id' | 'name'>[],
): string | null {
  if (assignment.kind === 'playlist') return playlists.find((p) => p.id === assignment.playlist_id)?.name ?? '플레이리스트';
  if (assignment.kind === 'schedule') return schedules.find((s) => s.id === assignment.schedule_id)?.name ?? '스케줄';
  return null;
}

/** 그룹을 지울 수 없게 막는 것 — 서버가 409 로 돌려준 details(`DELETE /api/v1/groups/:id`). */
export interface GroupDeleteBlockers {
  message: string;
  /** 들어 있는 화면 수. 화면을 다른 그룹으로 옮기거나 빼야 지울 수 있다. */
  displays: number;
  /** 이 그룹을 담당 범위로 맡은 계정 수. 지우면 그 계정이 고객사 전체를 맡게 될 수 있어 막는다. */
  users: number;
  /** 대상으로 고른 캠페인 수. */
  campaigns: number;
  /** 대상으로 고른, 나가고 있거나 나갈 긴급 송출 수. */
  emergencies: number;
  /** 대상으로 고른 캠페인·긴급의 이름(서버가 앞의 20개까지 싣는다). */
  references: Array<{ kind: 'campaign' | 'emergency'; id: string; name: string }>;
}

/**
 * 그룹 삭제가 쓰이는 곳 때문에 막혔으면 그 내역, 아니면 null(다른 오류는 평소처럼 문장으로 보인다).
 *
 * 서버는 막는 까닭마다 다른 키로 수를 준다 — 화면 `display_count`, 계정 `user_count`, 캠페인·긴급은
 * `campaign_count`·`emergency_count` 와 이름 목록 `references`. 서버는 한 번에 한 까닭만 말한다(화면 → 계정 →
 * 캠페인·긴급 순으로 먼저 걸린 것). 그래서 하나를 치우고 다시 지우면 다음 까닭이 나올 수 있다.
 */
export function groupDeleteBlockers(cause: unknown): GroupDeleteBlockers | null {
  if (!(cause instanceof ApiCallError) || cause.status !== 409) return null;
  const details = (cause.details ?? {}) as Record<string, unknown>;
  const references = Array.isArray(details.references)
    ? (details.references as GroupDeleteBlockers['references']).filter(
        (ref) => ref && (ref.kind === 'campaign' || ref.kind === 'emergency') && typeof ref.name === 'string',
      )
    : [];
  /** 수가 없으면 이름 목록에서 센다 — 옛 서버이거나 목록만 온 경우. */
  const count = (key: string, kind?: 'campaign' | 'emergency'): number => {
    const value = details[key];
    if (typeof value === 'number' && value > 0) return value;
    return kind ? references.filter((ref) => ref.kind === kind).length : 0;
  };
  const blockers: GroupDeleteBlockers = {
    message: cause.message,
    displays: count('display_count'),
    users: count('user_count'),
    campaigns: count('campaign_count', 'campaign'),
    emergencies: count('emergency_count', 'emergency'),
    references,
  };
  return blockers.displays + blockers.users + blockers.campaigns + blockers.emergencies > 0 ? blockers : null;
}

/** 앞의 이름 하나와 나머지 수 — 「가을 행사」 외 24개. */
function named(rows: GroupDeleteBlockers['references'], count: number, unit: string): string {
  const first = rows[0];
  if (!first) return `${count}${unit}`;
  return `「${first.name}」${count > 1 ? ` 외 ${count - 1}${unit}` : ''}`;
}

/**
 * 지울 수 없을 때 **먼저 무엇을 해야 하나** — 까닭마다 한 줄.
 *
 * "삭제할 수 없습니다" 만 말하면 사람은 화면·계정·캠페인을 차례로 열어 보게 된다. 서버는 한 번에 한 까닭만 주므로
 * (화면 → 계정 → 캠페인·긴급 순), 이것을 치우고 다시 지우면 다음 까닭이 나올 수 있다.
 */
export function blockerAdvice(blockers: GroupDeleteBlockers): string[] {
  const lines: string[] = [];
  if (blockers.displays > 0) {
    lines.push(`화면 ${blockers.displays}대가 들어 있습니다 — 그 화면들을 다른 그룹으로 옮기거나 그룹에서 빼세요.`);
  }
  if (blockers.users > 0) {
    lines.push(`이 그룹을 담당 범위로 맡은 계정이 ${blockers.users}명 있습니다 — [사용자] 에서 그 계정의 담당 범위를 먼저 바꾸세요.`);
  }
  if (blockers.campaigns > 0) {
    const rows = blockers.references.filter((ref) => ref.kind === 'campaign');
    lines.push(`대상으로 고른 캠페인 ${named(rows, blockers.campaigns, '개')} — 캠페인의 대상을 바꾸세요.`);
  }
  if (blockers.emergencies > 0) {
    const rows = blockers.references.filter((ref) => ref.kind === 'emergency');
    lines.push(`대상으로 고른 긴급 송출 ${named(rows, blockers.emergencies, '건')} — 해제하거나 끝난 뒤에 지우세요.`);
  }
  return lines;
}

/**
 * 그룹을 쓰는 곳 — 대상으로 고른 캠페인과, 담당 범위로 맡은 계정(2026-10-21, 화면 그룹 메뉴의 줄마다).
 *
 * 그룹은 화면·캠페인·계정 셋이 함께 쓰는데, 예전 그룹 관리 창은 화면 수만 보여 "이 그룹을 지워도 되나", "누가 맡고
 * 있나" 를 알려면 캠페인·사용자 화면을 돌아다녀야 했다. 계정 목록은 계정 관리 권한이 있을 때만 받으므로 `null` 이면
 * 모른다는 뜻이다.
 */
export function groupUsage(
  groupId: string,
  campaigns: readonly Pick<Campaign, 'name' | 'target'>[],
  users: readonly Pick<AccountUser, 'name' | 'scope_group_ids'>[] | null,
): { campaigns: string[]; users: string[] | null } {
  return {
    campaigns: campaigns
      .filter((campaign) => campaign.target.kind === 'groups' && campaign.target.group_ids.includes(groupId))
      .map((campaign) => campaign.name),
    users: users ? users.filter((user) => user.scope_group_ids.includes(groupId)).map((user) => user.name) : null,
  };
}

/** 이름 몇 개와 나머지 수 — "가을 행사, 신제품 외 3개". 없으면 빈 문자열. */
export function namesSummary(names: readonly string[], unit: string, shown = 2): string {
  if (names.length === 0) return '';
  const head = names.slice(0, shown).join(', ');
  return names.length > shown ? `${head} 외 ${names.length - shown}${unit}` : head;
}
